/**
 * PRIME Statistik Service (MIRA 2.0)
 *
 * snake_case → typed camelCase. Mappere eksporteret for unit-tests.
 * Endpoints: /portal/prime-stats/me · /salon · /admin/prime-stats/chain · /admin/prime-stats/salons/{id}
 */

import type {
  StatsTotals, DevelopmentPoint, MeStats, SalonEmployeeRow,
  SalonStats, ChainSalon, ChainStats,
} from '../types/primeStats.types';
import { apiGet } from './api';

// ---- Raw shapes (snake_case som returneret af MIRA) ----

interface RawTotals { total: number; primary: number; family: number; }
interface RawDev { year: number; month: number; total: number; primary: number; family: number; is_live?: boolean; }
interface RawMe { employee: { id: number; name: string }; totals: RawTotals; development: RawDev[]; }
interface RawEmp { employee_id: number | null; name: string; total: number; primary: number; family: number; }
interface RawSalon { salon: { id: number; name: string }; totals: RawTotals; by_employee: RawEmp[]; development: RawDev[]; }
interface RawChainSalon { id: number; name: string; city?: string | null; total: number; primary?: number; family?: number; }
interface RawChain { totals: RawTotals; unowned?: RawTotals | null; development: RawDev[]; salons?: RawChainSalon[] | null; }

// ---- Mappers (exported for tests) ----

function mapTotals(raw: RawTotals): StatsTotals {
  return { total: raw.total, primary: raw.primary, family: raw.family };
}

export function mapDevelopment(raw: RawDev[]): DevelopmentPoint[] {
  return raw.map((p) => ({
    year: p.year, month: p.month, total: p.total, primary: p.primary, family: p.family, isLive: !!p.is_live,
  }));
}

export function mapMeStats(raw: RawMe): MeStats {
  return {
    employee: { id: raw.employee.id, name: raw.employee.name },
    totals: mapTotals(raw.totals),
    development: mapDevelopment(raw.development),
  };
}

function mapEmployeeRow(raw: RawEmp): SalonEmployeeRow {
  return {
    employeeId: raw.employee_id,
    name: raw.name,
    total: raw.total,
    primary: raw.primary,
    family: raw.family,
    salonOwned: raw.employee_id == null,
  };
}

export function mapSalonStats(raw: RawSalon): SalonStats {
  return {
    salon: { id: raw.salon.id, name: raw.salon.name },
    totals: mapTotals(raw.totals),
    byEmployee: (raw.by_employee ?? []).map(mapEmployeeRow),
    development: mapDevelopment(raw.development),
  };
}

function mapChainSalon(raw: RawChainSalon): ChainSalon {
  return {
    id: raw.id, name: raw.name, city: raw.city ?? null,
    total: raw.total, primary: raw.primary ?? 0, family: raw.family ?? 0,
  };
}

export function mapChainStats(raw: RawChain): ChainStats {
  return {
    totals: mapTotals(raw.totals),
    unowned: raw.unowned ? mapTotals(raw.unowned) : { total: 0, primary: 0, family: 0 },
    development: mapDevelopment(raw.development),
    salons: (raw.salons ?? []).map(mapChainSalon),
  };
}

// ---- Raw shape exports for Task 3 (endpoint typing) ----
export type { RawMe, RawSalon, RawChain };

// ---- Endpoints ----

const enc = encodeURIComponent;

/** GET medarbejderens egne PRIME-tal. */
export async function getMyStats(): Promise<MeStats> {
  const data = await apiGet<RawMe>('/portal/prime-stats/me');
  return mapMeStats(data);
}

/** GET indehaverens salon-overblik. */
export async function getSalonStats(): Promise<SalonStats> {
  const data = await apiGet<RawSalon>('/portal/prime-stats/salon');
  return mapSalonStats(data);
}

/** GET admin salon-drill-down (samme form som /salon). */
export async function getSalonDrilldown(salonId: number): Promise<SalonStats> {
  const data = await apiGet<RawSalon>(`/portal/admin/prime-stats/salons/${enc(String(salonId))}`);
  return mapSalonStats(data);
}

/** GET kæde-overblik (admin). */
export async function getChainStats(): Promise<ChainStats> {
  const data = await apiGet<RawChain>('/portal/admin/prime-stats/chain');
  return mapChainStats(data);
}
